import { useState, useCallback, useMemo } from 'react';
import { generateId } from '../utils/measurements';
import { useHistory } from './useHistory';
import { createFloorFromTemplate } from '../utils/templateConverter';
import { detectRoomsFromWalls } from '../utils/roomDetection';
import { DEFAULT_POST } from '../constants/posts';

// Initial floor template
const createEmptyFloor = (id, name, floorElevation = 0) => ({
  id,
  name,
  floorElevation, // Height from grade in inches (0 for ground floor, 108 for 9' above, etc.)
  walls: [],
  doors: [],
  windows: [],
  furniture: [],
  rooms: [],
  stairs: [],
  electrical: [],
  plumbing: [],
  plumbingLines: [],
  fireSafety: [],
  posts: [],
  panels: [],
  dimensions: [],
  texts: [],
  lines: [],
  polylines: [],
  hatches: [],
  porches: [],
  roofs: [],
  joists: [],
});

// Bring a floor loaded from a file up to the current shape. Files saved before a
// collection existed simply lack it - a plan drawn before posts were added has no
// `posts` key - and the renderers iterate these arrays without guarding, so a
// missing one is a crash rather than an empty layer. Backfilling on the way in
// means old files keep opening as new collections are added.
const normalizeFloor = (floor) => {
  const template = createEmptyFloor(floor?.id, floor?.name);
  const normalized = { ...template, ...floor };

  for (const key of Object.keys(template)) {
    if (Array.isArray(template[key]) && !Array.isArray(normalized[key])) {
      normalized[key] = [];
    }
  }

  return normalized;
};

// Selection type -> the floor collection it lives in. Used by the clipboard.
const TYPE_TO_COLLECTION = {
  wall: 'walls',
  door: 'doors',
  window: 'windows',
  furniture: 'furniture',
  fireSafety: 'fireSafety',
  post: 'posts',
  panel: 'panels',
  room: 'rooms',
  roof: 'roofs',
  dimension: 'dimensions',
  line: 'lines',
  text: 'texts',
  polyline: 'polylines',
  hatch: 'hatches',
};

// Doors and windows have no coordinates of their own. They are stored as a
// wall id plus a 0-1 position along that wall, so they can only be pasted
// onto a wall - never at an arbitrary point.
const WALL_BOUND_TYPES = ['door', 'window'];

// True for a { x, y } pair. Guards against door/window `position`, which is a
// number (0-1 along a wall) rather than a point.
const isPoint = (value) => (
  !!value && typeof value === 'object'
  && typeof value.x === 'number' && typeof value.y === 'number'
);

// Every coordinate an item defines. Items use four different shapes:
//   x / y          furniture, fireSafety
//   position       texts (a point - unlike doors and windows, where it is a number)
//   start / end    walls, lines, dimensions
//   points[]       rooms, roofs, polylines, hatches
const itemPoints = (item) => {
  const points = [];
  if (typeof item.x === 'number' && typeof item.y === 'number') points.push({ x: item.x, y: item.y });
  if (isPoint(item.position)) points.push(item.position);
  if (isPoint(item.start)) points.push(item.start);
  if (isPoint(item.end)) points.push(item.end);
  if (Array.isArray(item.points)) item.points.forEach(p => isPoint(p) && points.push(p));
  return points;
};

// Move an item by (dx, dy), covering all four coordinate shapes.
const translateItem = (item, dx, dy) => {
  const moved = { ...item };
  const shift = (p) => ({ ...p, x: p.x + dx, y: p.y + dy });

  if (typeof moved.x === 'number') moved.x += dx;
  if (typeof moved.y === 'number') moved.y += dy;
  if (isPoint(moved.position)) moved.position = shift(moved.position);
  if (isPoint(moved.start)) moved.start = shift(moved.start);
  if (isPoint(moved.end)) moved.end = shift(moved.end);
  if (Array.isArray(moved.points)) moved.points = moved.points.map(p => (isPoint(p) ? shift(p) : p));

  return moved;
};

/**
 * useFloorPlan hook for managing floor plan state
 *
 * @returns {object} - Floor plan state and actions
 */
export const useFloorPlan = () => {
  // Floors with history
  const {
    state: floors,
    setState: setFloors,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  } = useHistory([createEmptyFloor('floor-1', 'Floor 1')]);

  // Active floor ID
  const [activeFloorId, setActiveFloorId] = useState('floor-1');

  // Selection state
  const [selectedItems, setSelectedItems] = useState([]);

  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState(null);

  // PDF layer state (background reference layer)
  const [pdfLayer, setPdfLayer] = useState({
    dataUrl: null,        // PDF rendered as image (current page)
    opacity: 0.7,         // Default 70% opacity
    scale: null,          // pixels per inch after calibration
    position: { x: 0, y: 0 },
    visible: true,
    width: 0,
    height: 0,
    fileName: null,
    // Multi-page support
    pdfDoc: null,         // pdf.js document reference
    currentPage: 1,       // Current page number (1-indexed)
    pageCount: 1,         // Total number of pages
    renderScale: 2,       // Scale used for rendering (for quality)
    pageScales: {},       // Per-page scale calibrations { pageNum: scaleRatio }
  });

  // Annotations state (markup tools for PDF)
  const [annotations, setAnnotations] = useState([]);
  const [annotationSettings, setAnnotationSettings] = useState({
    color: '#ff0000',       // Default red
    thickness: 4,           // Default 4px
    aboveFloorPlan: true,   // Render above floor plan elements
    stampType: 'approved',  // Default stamp type
    customStampText: '',    // Custom stamp text for custom type
    cloudLabel: '',         // Label for revision clouds (e.g., "REV 1")
  });

  // PDF measurements state (linear and area measurements)
  const [pdfMeasurements, setPdfMeasurements] = useState([]);

  // Get active floor
  const activeFloor = useMemo(() => {
    return floors.find(f => f.id === activeFloorId) || floors[0];
  }, [floors, activeFloorId]);

  // Update active floor
  const updateActiveFloor = useCallback((updater) => {
    setFloors(prev => prev.map(floor =>
      floor.id === activeFloorId
        ? (typeof updater === 'function' ? updater(floor) : { ...floor, ...updater })
        : floor
    ));
  }, [activeFloorId, setFloors]);

  // Add floor
  const addFloor = useCallback((name, floorElevation = 0) => {
    const id = generateId();
    setFloors(prev => [...prev, createEmptyFloor(id, name || `Floor ${prev.length + 1}`, floorElevation)]);
    setActiveFloorId(id);
    return id;
  }, [setFloors]);

  // Update floor elevation (height from grade)
  const updateFloorElevation = useCallback((floorId, newElevation) => {
    setFloors(prev => prev.map(f =>
      f.id === floorId ? { ...f, floorElevation: newElevation } : f
    ));
  }, [setFloors]);

  // Update window on any floor (for elevation view where all floors are visible)
  const updateWindowOnFloor = useCallback((floorId, windowId, updates) => {
    setFloors(prev => prev.map(f =>
      f.id === floorId
        ? { ...f, windows: f.windows.map(w => w.id === windowId ? { ...w, ...updates } : w) }
        : f
    ));
  }, [setFloors]);

  // Remove window from any floor
  const removeWindowOnFloor = useCallback((floorId, windowId) => {
    setFloors(prev => prev.map(f =>
      f.id === floorId
        ? { ...f, windows: f.windows.filter(w => w.id !== windowId) }
        : f
    ));
  }, [setFloors]);

  // Update door on any floor
  const updateDoorOnFloor = useCallback((floorId, doorId, updates) => {
    setFloors(prev => prev.map(f =>
      f.id === floorId
        ? { ...f, doors: f.doors.map(d => d.id === doorId ? { ...d, ...updates } : d) }
        : f
    ));
  }, [setFloors]);

  // Remove door from any floor
  const removeDoorOnFloor = useCallback((floorId, doorId) => {
    setFloors(prev => prev.map(f =>
      f.id === floorId
        ? { ...f, doors: f.doors.filter(d => d.id !== doorId) }
        : f
    ));
  }, [setFloors]);

  // Remove floor
  const removeFloor = useCallback((floorId) => {
    if (floors.length <= 1) return; // Keep at least one floor

    setFloors(prev => {
      const newFloors = prev.filter(f => f.id !== floorId);
      if (activeFloorId === floorId) {
        setActiveFloorId(newFloors[0].id);
      }
      return newFloors;
    });
  }, [floors.length, activeFloorId, setFloors]);

  // Duplicate floor
  const duplicateFloor = useCallback((floorId) => {
    const floor = floors.find(f => f.id === floorId);
    if (!floor) return;

    const newId = generateId();
    const newFloor = {
      ...JSON.parse(JSON.stringify(floor)),
      id: newId,
      name: `${floor.name} (Copy)`,
    };

    // Build a mapping from old wall IDs to new wall IDs
    const wallIdMap = new Map();
    const oldWalls = newFloor.walls || [];

    // Generate new IDs for walls and track the mapping
    newFloor.walls = oldWalls.map(wall => {
      const newWallId = generateId();
      wallIdMap.set(wall.id, newWallId);
      return { ...wall, id: newWallId };
    });

    // Generate new IDs for doors and remap wallId references
    newFloor.doors = (newFloor.doors || []).map(door => ({
      ...door,
      id: generateId(),
      wallId: wallIdMap.get(door.wallId) || door.wallId, // Remap to new wall ID
    }));

    // Generate new IDs for windows and remap wallId references
    newFloor.windows = (newFloor.windows || []).map(window => ({
      ...window,
      id: generateId(),
      wallId: wallIdMap.get(window.wallId) || window.wallId, // Remap to new wall ID
    }));

    // Generate new IDs for all other items
    const regenerateIds = (items) => (items || []).map(item => ({ ...item, id: generateId() }));

    newFloor.furniture = regenerateIds(newFloor.furniture);
    newFloor.rooms = regenerateIds(newFloor.rooms);
    newFloor.stairs = regenerateIds(newFloor.stairs);
    newFloor.electrical = regenerateIds(newFloor.electrical);
    newFloor.plumbing = regenerateIds(newFloor.plumbing);
    newFloor.plumbingLines = regenerateIds(newFloor.plumbingLines);
    newFloor.fireSafety = regenerateIds(newFloor.fireSafety);
    newFloor.dimensions = regenerateIds(newFloor.dimensions);
    newFloor.texts = regenerateIds(newFloor.texts);
    newFloor.lines = regenerateIds(newFloor.lines);
    newFloor.polylines = regenerateIds(newFloor.polylines);
    newFloor.hatches = regenerateIds(newFloor.hatches);
    newFloor.porches = regenerateIds(newFloor.porches);
    newFloor.roofs = regenerateIds(newFloor.roofs);

    setFloors(prev => [...prev, newFloor]);
    setActiveFloorId(newId);
    return newId;
  }, [floors, setFloors]);

  // Rename floor
  const renameFloor = useCallback((floorId, newName) => {
    setFloors(prev => prev.map(f =>
      f.id === floorId ? { ...f, name: newName } : f
    ));
  }, [setFloors]);

  // Add wall
  const addWall = useCallback((wall) => {
    const newWall = { ...wall, id: wall.id || generateId() };
    updateActiveFloor(floor => ({ ...floor, walls: [...floor.walls, newWall] }));
    return newWall.id;
  }, [updateActiveFloor]);

  // Update wall
  const updateWall = useCallback((wallId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      walls: floor.walls.map(w => w.id === wallId ? { ...w, ...updates } : w),
    }));
  }, [updateActiveFloor]);

  // Remove wall
  const removeWall = useCallback((wallId) => {
    updateActiveFloor(floor => ({
      ...floor,
      walls: floor.walls.filter(w => w.id !== wallId),
      // Also remove doors and windows on this wall
      doors: floor.doors.filter(d => d.wallId !== wallId),
      windows: floor.windows.filter(w => w.wallId !== wallId),
    }));
  }, [updateActiveFloor]);

  // Add door
  const addDoor = useCallback((door) => {
    const newDoor = { ...door, id: door.id || generateId() };
    updateActiveFloor(floor => ({ ...floor, doors: [...floor.doors, newDoor] }));
    return newDoor.id;
  }, [updateActiveFloor]);

  // Update door
  const updateDoor = useCallback((doorId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      doors: floor.doors.map(d => d.id === doorId ? { ...d, ...updates } : d),
    }));
  }, [updateActiveFloor]);

  // Remove door
  const removeDoor = useCallback((doorId) => {
    updateActiveFloor(floor => ({
      ...floor,
      doors: floor.doors.filter(d => d.id !== doorId),
    }));
  }, [updateActiveFloor]);

  // Add window
  const addWindow = useCallback((window) => {
    const newWindow = { ...window, id: window.id || generateId() };
    updateActiveFloor(floor => ({ ...floor, windows: [...floor.windows, newWindow] }));
    return newWindow.id;
  }, [updateActiveFloor]);

  // Update window
  const updateWindow = useCallback((windowId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      windows: floor.windows.map(w => w.id === windowId ? { ...w, ...updates } : w),
    }));
  }, [updateActiveFloor]);

  // Remove window
  const removeWindow = useCallback((windowId) => {
    updateActiveFloor(floor => ({
      ...floor,
      windows: floor.windows.filter(w => w.id !== windowId),
    }));
  }, [updateActiveFloor]);

  // Add furniture
  const addFurniture = useCallback((furniture) => {
    // Always generate a unique ID - furniture.id from library is just the template type
    const newFurniture = { ...furniture, id: generateId(), templateId: furniture.id };
    updateActiveFloor(floor => ({ ...floor, furniture: [...floor.furniture, newFurniture] }));
    return newFurniture.id;
  }, [updateActiveFloor]);

  // Update furniture
  const updateFurniture = useCallback((furnitureId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      furniture: floor.furniture.map(f => f.id === furnitureId ? { ...f, ...updates } : f),
    }));
  }, [updateActiveFloor]);

  // Remove furniture
  const removeFurniture = useCallback((furnitureId) => {
    updateActiveFloor(floor => ({
      ...floor,
      furniture: floor.furniture.filter(f => f.id !== furnitureId),
    }));
  }, [updateActiveFloor]);

  // Add fire/life-safety symbol
  const addFireSafety = useCallback((item) => {
    const newItem = { ...item, id: generateId(), templateId: item.id };
    updateActiveFloor(floor => ({
      ...floor,
      fireSafety: [...(floor.fireSafety || []), newItem],
    }));
    return newItem.id;
  }, [updateActiveFloor]);

  // Update fire/life-safety symbol
  const updateFireSafety = useCallback((itemId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      fireSafety: (floor.fireSafety || []).map(f => f.id === itemId ? { ...f, ...updates } : f),
    }));
  }, [updateActiveFloor]);

  // Remove fire/life-safety symbol
  const removeFireSafety = useCallback((itemId) => {
    updateActiveFloor(floor => ({
      ...floor,
      fireSafety: (floor.fireSafety || []).filter(f => f.id !== itemId),
    }));
  }, [updateActiveFloor]);

  // Add porch/structural post
  const addPost = useCallback((post) => {
    const newPost = { ...DEFAULT_POST, ...post, id: generateId() };
    updateActiveFloor(floor => ({
      ...floor,
      posts: [...(floor.posts || []), newPost],
    }));
    return newPost.id;
  }, [updateActiveFloor]);

  // Update porch/structural post
  const updatePost = useCallback((postId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      posts: (floor.posts || []).map(p => p.id === postId ? { ...p, ...updates } : p),
    }));
  }, [updateActiveFloor]);

  // Remove porch/structural post
  const removePost = useCallback((postId) => {
    updateActiveFloor(floor => ({
      ...floor,
      posts: (floor.posts || []).filter(p => p.id !== postId),
    }));
  }, [updateActiveFloor]);

  // Add room
  const addRoom = useCallback((room) => {
    const newRoom = { ...room, id: room.id || generateId() };
    updateActiveFloor(floor => ({ ...floor, rooms: [...floor.rooms, newRoom] }));
    return newRoom.id;
  }, [updateActiveFloor]);

  // Update room
  const updateRoom = useCallback((roomId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      rooms: floor.rooms.map(r => r.id === roomId ? { ...r, ...updates } : r),
    }));
  }, [updateActiveFloor]);

  // Remove room
  const removeRoom = useCallback((roomId) => {
    updateActiveFloor(floor => ({
      ...floor,
      rooms: floor.rooms.filter(r => r.id !== roomId),
    }));
  }, [updateActiveFloor]);

  // Detect rooms from walls
  const detectRooms = useCallback(() => {
    updateActiveFloor(floor => {
      const detectedRooms = detectRoomsFromWalls(floor.walls, floor.rooms);
      if (detectedRooms.length === 0) return floor;
      return {
        ...floor,
        rooms: [...floor.rooms, ...detectedRooms],
      };
    });
  }, [updateActiveFloor]);

  // Add roof
  const addRoof = useCallback((roof) => {
    const newRoof = { ...roof, id: roof.id || generateId() };
    updateActiveFloor(floor => ({ ...floor, roofs: [...floor.roofs, newRoof] }));
    return newRoof.id;
  }, [updateActiveFloor]);

  // Update roof
  const updateRoof = useCallback((roofId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      roofs: floor.roofs.map(r => r.id === roofId ? { ...r, ...updates } : r),
    }));
  }, [updateActiveFloor]);

  // Remove roof
  const removeRoof = useCallback((roofId) => {
    updateActiveFloor(floor => ({
      ...floor,
      roofs: floor.roofs.filter(r => r.id !== roofId),
    }));
  }, [updateActiveFloor]);

  // Generic add item
  const addItem = useCallback((type, item) => {
    const newItem = { ...item, id: item.id || generateId() };
    updateActiveFloor(floor => ({
      ...floor,
      [type]: [...(floor[type] || []), newItem],
    }));
    return newItem.id;
  }, [updateActiveFloor]);

  // Generic update item
  const updateItem = useCallback((type, itemId, updates) => {
    updateActiveFloor(floor => ({
      ...floor,
      [type]: (floor[type] || []).map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    }));
  }, [updateActiveFloor]);

  // Generic remove item
  const removeItem = useCallback((type, itemId) => {
    updateActiveFloor(floor => ({
      ...floor,
      [type]: (floor[type] || []).filter(item => item.id !== itemId),
    }));
  }, [updateActiveFloor]);

  /**
   * Copy the current selection to the clipboard.
   *
   * @returns {number} how many items were copied
   */
  const copySelected = useCallback(() => {
    if (!selectedItems || selectedItems.length === 0) return 0;

    const copiedItems = selectedItems.map(sel => {
      const collection = TYPE_TO_COLLECTION[sel.type];
      if (!collection) return null;

      // Selections normally carry the item as sel.item, but a few callers
      // (Select All) pass only an id. Look the item up either way, and prefer
      // the live object so edits made since selecting are included.
      const itemId = sel.item?.id ?? sel.id;
      const live = (activeFloor?.[collection] || []).find(i => i.id === itemId);
      const source = live || sel.item;

      return source ? { type: sel.type, item: JSON.parse(JSON.stringify(source)) } : null;
    }).filter(Boolean);

    if (copiedItems.length > 0) setClipboard(copiedItems);
    return copiedItems.length;
  }, [selectedItems, activeFloor]);

  /**
   * Paste the clipboard onto the active floor.
   *
   * @param {{x: number, y: number}} target - where the copy lands, in world
   *        coordinates. The copied group is centered on this point.
   * @param {object} [options]
   * @param {{wall: object, position: number}|null} [options.wallAtTarget] - the
   *        wall under the cursor, used to re-home a door or window whose own
   *        wall was not part of the copy.
   * @returns {{pasted: number, skipped: number}}
   */
  const paste = useCallback((target, options = {}) => {
    if (!clipboard || clipboard.length === 0) return { pasted: 0, skipped: 0 };

    const { wallAtTarget = null } = options;
    const positioned = clipboard.filter(c => !WALL_BOUND_TYPES.includes(c.type));
    const wallBound = clipboard.filter(c => WALL_BOUND_TYPES.includes(c.type));

    // Shift the group so its bounding-box center lands on the target point.
    let dx = 0;
    let dy = 0;
    if (target) {
      const points = positioned.flatMap(c => itemPoints(c.item));
      if (points.length > 0) {
        const xs = points.map(p => p.x);
        const ys = points.map(p => p.y);
        dx = target.x - (Math.min(...xs) + Math.max(...xs)) / 2;
        dy = target.y - (Math.min(...ys) + Math.max(...ys)) / 2;
      }
    }

    const idMap = new Map(); // original id -> pasted id, for relinking
    const additions = {};    // collection -> items to append
    const newSelection = [];
    let skipped = 0;

    const queue = (type, item) => {
      const collection = TYPE_TO_COLLECTION[type];
      if (!collection) {
        skipped++;
        return;
      }
      if (!additions[collection]) additions[collection] = [];
      additions[collection].push(item);
      newSelection.push({ type, item });
    };

    // Positioned items first, so walls are in idMap before anything links to them.
    positioned.forEach(({ type, item }) => {
      const copy = translateItem({ ...item, id: generateId() }, dx, dy);
      idMap.set(item.id, copy.id);
      queue(type, copy);
    });

    // Rooms reference the walls that form them. Point those at the copied walls
    // where all of them came along; otherwise drop the link, so a pasted room
    // never references the originals it was copied from.
    Object.values(additions).flat().forEach(item => {
      if (!Array.isArray(item.wallIds)) return;
      const relinked = item.wallIds.map(id => idMap.get(id)).filter(Boolean);
      if (relinked.length === item.wallIds.length) {
        item.wallIds = relinked;
      } else {
        delete item.wallIds;
      }
    });

    // Doors and windows ride on a wall: keep them on their own wall if it was
    // copied too, otherwise attach them to the wall under the cursor.
    wallBound.forEach(({ type, item }) => {
      const copiedWallId = idMap.get(item.wallId);
      if (copiedWallId) {
        queue(type, { ...item, id: generateId(), wallId: copiedWallId });
      } else if (wallAtTarget?.wall) {
        queue(type, {
          ...item,
          id: generateId(),
          wallId: wallAtTarget.wall.id,
          position: wallAtTarget.position,
        });
      } else {
        skipped++; // no wall to attach to
      }
    });

    const pasted = Object.values(additions).reduce((total, items) => total + items.length, 0);
    if (pasted === 0) return { pasted: 0, skipped };

    // A single update, so one undo reverses the whole paste.
    updateActiveFloor(floor => {
      const next = { ...floor };
      Object.entries(additions).forEach(([collection, items]) => {
        next[collection] = [...(floor[collection] || []), ...items];
      });
      return next;
    });

    setSelectedItems(newSelection);
    return { pasted, skipped };
  }, [clipboard, updateActiveFloor]);

  // Delete selected items - requires items to be passed to avoid stale closure
  const deleteSelected = useCallback((itemsToDelete) => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;

    itemsToDelete.forEach(sel => {
      const typeMap = {
        wall: 'walls',
        door: 'doors',
        window: 'windows',
        furniture: 'furniture',
        room: 'rooms',
        roof: 'roofs',
        dimension: 'dimensions',
        line: 'lines',
        text: 'texts',
      };
      const type = typeMap[sel.type] || sel.type;
      // Get the item id - selection objects store the id inside sel.item.id
      const itemId = sel.item?.id || sel.id;

      if (sel.type === 'wall') {
        removeWall(itemId);
      } else {
        removeItem(type, itemId);
      }
    });

    setSelectedItems([]);
  }, [removeWall, removeItem, setSelectedItems]);

  // Clear floor
  const clearFloor = useCallback(() => {
    updateActiveFloor(floor => ({
      ...floor,
      walls: [],
      doors: [],
      windows: [],
      furniture: [],
      rooms: [],
      stairs: [],
      electrical: [],
      plumbing: [],
      plumbingLines: [],
      fireSafety: [],
      dimensions: [],
      texts: [],
      lines: [],
      polylines: [],
      hatches: [],
      porches: [],
      roofs: [],
    }));
    setSelectedItems([]);
  }, [updateActiveFloor]);

  // New file (reset everything)
  const newFile = useCallback(() => {
    setFloors([createEmptyFloor('floor-1', 'Floor 1')]);
    setActiveFloorId('floor-1');
    setSelectedItems([]);
    setClipboard(null);
    clearHistory();
  }, [setFloors, clearHistory]);

  // Load from template
  const loadFromTemplate = useCallback((template, scaleFactor = 1) => {
    const newFloor = createFloorFromTemplate(template, scaleFactor, 'Floor 1');
    setFloors([newFloor]);
    setActiveFloorId(newFloor.id);
    setSelectedItems([]);
    setClipboard(null);
    clearHistory();
    return newFloor;
  }, [setFloors, clearHistory]);

  // Import PDF layer
  const importPdfLayer = useCallback((pdfData) => {
    setPdfLayer({
      dataUrl: pdfData.dataUrl,
      opacity: pdfData.geo ? 1 : 0.7,
      // Georeferenced images arrive already scaled from their world file
      scale: pdfData.scale || null,
      originalWidth: pdfData.imageWidth,
      originalHeight: pdfData.imageHeight,
      geo: pdfData.geo || null,
      position: { x: 0, y: 0 },
      visible: true,
      width: pdfData.width,
      height: pdfData.height,
      fileName: pdfData.fileName,
      // Multi-page support
      pdfDoc: pdfData.pdfDoc || null,
      currentPage: pdfData.pageNumber || 1,
      pageCount: pdfData.pageCount || 1,
      renderScale: pdfData.renderScale || 2,
      pageScales: {},  // Reset per-page scales on new import
      // Store raw PDF bytes for export
      pdfArrayBuffer: pdfData.pdfArrayBuffer || null,
    });
  }, []);

  // Update PDF layer
  const updatePdfLayer = useCallback((updates) => {
    setPdfLayer(prev => ({ ...prev, ...updates }));
  }, []);

  // Remove PDF layer
  const removePdfLayer = useCallback(() => {
    setPdfLayer({
      dataUrl: null,
      opacity: 0.7,
      scale: null,
      position: { x: 0, y: 0 },
      visible: true,
      width: 0,
      height: 0,
      fileName: null,
      geo: null,
      pdfDoc: null,
      currentPage: 1,
      pageCount: 1,
      renderScale: 2,
      pageScales: {},
    });
  }, []);

  // Calibrate PDF scale - resizes the PDF so traced measurements are correct
  // The ratio represents: 1 drawing inch = ratio real-world inches
  // For 1"=40', ratio = 480 (40 feet * 12 inches/foot)
  const calibratePdfScale = useCallback((scaleRatio) => {
    setPdfLayer(prev => {
      // Floor plan coordinate system: 40 pixels = 1 foot = 12 inches
      // So 1 real-world inch = 40/12 ≈ 3.333 floor plan pixels
      const FLOOR_PLAN_PX_PER_INCH = 40 / 12;

      // PDF DPI: PDFs are rendered at scale 1 = 72 DPI (72 pixels per inch)
      // The PDF was imported with width/height from viewport at scale 1
      // So the PDF has 72 pixels per drawing inch
      const PDF_DPI = 72;

      // At scale ratio, 1 drawing inch = scaleRatio real-world inches
      // We need 1 drawing inch on PDF to equal scaleRatio real inches in floor plan coords
      // scaleRatio inches * 3.333 px/inch = floor plan pixels per drawing inch
      const floorPlanPxPerDrawingInch = scaleRatio * FLOOR_PLAN_PX_PER_INCH;

      // Current PDF has 72 pixels per drawing inch
      // We need to scale it so that 72 PDF pixels maps to floorPlanPxPerDrawingInch
      // Scale factor = desired / current = floorPlanPxPerDrawingInch / PDF_DPI
      const scaleFactor = floorPlanPxPerDrawingInch / PDF_DPI;

      // Calculate new dimensions
      // Original dimensions were from viewport scale 1, so they're in 72 DPI
      const originalWidth = prev.originalWidth || prev.width;
      const originalHeight = prev.originalHeight || prev.height;

      // Store per-page scale for multi-page support
      const updatedPageScales = {
        ...prev.pageScales,
        [prev.currentPage]: scaleRatio,
      };

      return {
        ...prev,
        scale: scaleRatio,
        originalWidth,  // Preserve original dimensions for recalibration
        originalHeight,
        width: originalWidth * scaleFactor,
        height: originalHeight * scaleFactor,
        pageScales: updatedPageScales,
      };
    });
  }, []);

  // Change PDF page - renders a new page from the PDF document
  const changePdfPage = useCallback(async (pageNumber) => {
    const pdfDoc = pdfLayer.pdfDoc;
    if (!pdfDoc || pageNumber < 1 || pageNumber > pdfLayer.pageCount) return;

    try {
      const page = await pdfDoc.getPage(pageNumber);
      const renderScale = pdfLayer.renderScale || 2;
      // Apply rotation if set for this page
      const rotation = pdfLayer.pageRotations?.[pageNumber] || 0;
      const viewport = page.getViewport({ scale: renderScale, rotation });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      const dataUrl = canvas.toDataURL('image/png');
      const originalViewport = page.getViewport({ scale: 1, rotation });

      // Check if this page has a saved scale calibration
      const savedScale = pdfLayer.pageScales[pageNumber];

      // Calculate dimensions based on saved scale or use original
      let newWidth = originalViewport.width;
      let newHeight = originalViewport.height;

      if (savedScale) {
        const FLOOR_PLAN_PX_PER_INCH = 40 / 12;
        const PDF_DPI = 72;
        const scaleFactor = (savedScale * FLOOR_PLAN_PX_PER_INCH) / PDF_DPI;
        newWidth = originalViewport.width * scaleFactor;
        newHeight = originalViewport.height * scaleFactor;
      }

      setPdfLayer(prev => ({
        ...prev,
        dataUrl,
        currentPage: pageNumber,
        width: newWidth,
        height: newHeight,
        originalWidth: originalViewport.width,
        originalHeight: originalViewport.height,
        scale: savedScale || null,
        // Reset position when changing pages, but keep other settings
        position: { x: 0, y: 0 },
      }));
    } catch (err) {
      console.error('Error changing PDF page:', err);
    }
  }, [pdfLayer.pdfDoc, pdfLayer.pageCount, pdfLayer.renderScale, pdfLayer.pageScales, pdfLayer.pageRotations]);

  // Re-link PDF document - restores pdfDoc reference after load from save
  // This preserves position, scale, calibration etc but restores page navigation
  const relinkPdfDocument = useCallback((pdfDoc, pageCount, arrayBuffer = null) => {
    setPdfLayer(prev => ({
      ...prev,
      pdfDoc: pdfDoc,
      pageCount: pageCount || prev.pageCount,
      pdfArrayBuffer: arrayBuffer || prev.pdfArrayBuffer,
    }));
  }, []);

  // Rotate a PDF page (adds rotation to the page's current rotation)
  // If rotating the current page, re-renders it with the new rotation
  const rotatePdfPage = useCallback(async (pageNum, rotationDelta) => {
    const pdfDoc = pdfLayer.pdfDoc;
    const currentPage = pdfLayer.currentPage;
    const currentRotation = pdfLayer.pageRotations?.[pageNum] || 0;

    // Normalize to 0, 90, 180, 270
    let newRotation = (currentRotation + rotationDelta) % 360;
    if (newRotation < 0) newRotation += 360;

    // Update the rotation state
    setPdfLayer(prev => ({
      ...prev,
      pageRotations: {
        ...prev.pageRotations,
        [pageNum]: newRotation,
      },
    }));

    // If rotating the current page and we have a pdfDoc, re-render it
    if (pageNum === currentPage && pdfDoc) {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const renderScale = pdfLayer.renderScale || 2;
        const viewport = page.getViewport({ scale: renderScale, rotation: newRotation });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        const dataUrl = canvas.toDataURL('image/png');
        const originalViewport = page.getViewport({ scale: 1, rotation: newRotation });

        // Check if this page has a saved scale calibration
        const savedScale = pdfLayer.pageScales?.[pageNum];
        let newWidth = originalViewport.width;
        let newHeight = originalViewport.height;

        if (savedScale) {
          const FLOOR_PLAN_PX_PER_INCH = 40 / 12;
          const PDF_DPI = 72;
          const scaleFactor = (savedScale * FLOOR_PLAN_PX_PER_INCH) / PDF_DPI;
          newWidth = originalViewport.width * scaleFactor;
          newHeight = originalViewport.height * scaleFactor;
        }

        setPdfLayer(prev => ({
          ...prev,
          dataUrl,
          width: newWidth,
          height: newHeight,
          originalWidth: originalViewport.width,
          originalHeight: originalViewport.height,
        }));
      } catch (err) {
        console.error('Error re-rendering rotated page:', err);
      }
    }
  }, [pdfLayer.pdfDoc, pdfLayer.currentPage, pdfLayer.pageRotations, pdfLayer.renderScale, pdfLayer.pageScales]);

  // Delete pages from PDF (virtual deletion - tracks which pages to skip)
  // Note: This is a soft delete that tracks which pages are "deleted" in the project
  // The original PDF is not modified
  const deletePdfPages = useCallback((pagesToDelete) => {
    if (!pagesToDelete || pagesToDelete.length === 0) return;

    setPdfLayer(prev => {
      const deletedSet = new Set([...(prev.deletedPages || []), ...pagesToDelete]);
      const remainingPages = prev.pageCount - deletedSet.size;

      if (remainingPages < 1) {
        console.warn('Cannot delete all pages');
        return prev;
      }

      // If current page is deleted, switch to first available page
      let newCurrentPage = prev.currentPage;
      if (deletedSet.has(prev.currentPage)) {
        for (let i = 1; i <= prev.pageCount; i++) {
          if (!deletedSet.has(i)) {
            newCurrentPage = i;
            break;
          }
        }
      }

      return {
        ...prev,
        deletedPages: Array.from(deletedSet),
        currentPage: newCurrentPage,
      };
    });
  }, []);

  // Add a blank page for sketching (virtual page, not actually in the PDF)
  const addBlankPdfPage = useCallback((afterPage) => {
    setPdfLayer(prev => {
      const blankPages = prev.blankPages || [];
      const newBlankPage = {
        id: generateId(),
        insertAfter: afterPage,
        width: prev.width || 800,
        height: prev.height || 600,
        createdAt: new Date().toISOString(),
      };

      return {
        ...prev,
        blankPages: [...blankPages, newBlankPage],
        // Update page count to include blank pages
        virtualPageCount: (prev.virtualPageCount || prev.pageCount) + 1,
      };
    });
  }, []);

  // Extract pages to a new project structure (returns data for new project)
  const extractPdfPages = useCallback((pageNumbers) => {
    // This creates data that can be used to start a new project
    // The actual "new project" creation happens in App.jsx
    return {
      sourceFileName: pdfLayer.fileName,
      pages: pageNumbers.sort((a, b) => a - b),
      pageScales: pageNumbers.reduce((acc, p) => {
        if (pdfLayer.pageScales?.[p]) {
          acc[p] = pdfLayer.pageScales[p];
        }
        return acc;
      }, {}),
      pageRotations: pageNumbers.reduce((acc, p) => {
        if (pdfLayer.pageRotations?.[p]) {
          acc[p] = pdfLayer.pageRotations[p];
        }
        return acc;
      }, {}),
      pdfArrayBuffer: pdfLayer.pdfArrayBuffer,
      pdfDoc: pdfLayer.pdfDoc,
    };
  }, [pdfLayer]);

  // Add annotation
  const addAnnotation = useCallback((annotation) => {
    const newAnnotation = { ...annotation, id: generateId() };
    setAnnotations(prev => [...prev, newAnnotation]);
    return newAnnotation.id;
  }, []);

  // Update annotation
  const updateAnnotation = useCallback((annotationId, updates) => {
    setAnnotations(prev =>
      prev.map(a => a.id === annotationId ? { ...a, ...updates } : a)
    );
  }, []);

  // Remove annotation
  const removeAnnotation = useCallback((annotationId) => {
    setAnnotations(prev => prev.filter(a => a.id !== annotationId));
  }, []);

  // Clear all annotations
  const clearAnnotations = useCallback(() => {
    setAnnotations([]);
  }, []);

  // Update annotation settings
  const updateAnnotationSettings = useCallback((updates) => {
    setAnnotationSettings(prev => ({ ...prev, ...updates }));
  }, []);

  // Add PDF measurement
  const addPdfMeasurement = useCallback((measurement) => {
    const newMeasurement = { ...measurement, id: measurement.id || generateId() };
    setPdfMeasurements(prev => [...prev, newMeasurement]);
    return newMeasurement.id;
  }, []);

  // Update PDF measurement
  const updatePdfMeasurement = useCallback((measurementId, updates) => {
    setPdfMeasurements(prev => prev.map(m =>
      m.id === measurementId ? { ...m, ...updates } : m
    ));
  }, []);

  // Remove PDF measurement
  const removePdfMeasurement = useCallback((measurementId) => {
    setPdfMeasurements(prev => prev.filter(m => m.id !== measurementId));
  }, []);

  // Clear all PDF measurements
  const clearPdfMeasurements = useCallback(() => {
    setPdfMeasurements([]);
  }, []);

  // Export to JSON (accepts optional extras like estimates)
  const exportToJSON = useCallback((extras = {}) => {
    // Exclude pdfDoc from serialization as it's a pdf.js object that can't be JSON stringified
    // The PDF will need to be re-imported when loading a saved project
    const serializablePdfLayer = pdfLayer.dataUrl ? {
      ...pdfLayer,
      pdfDoc: null,  // Can't serialize pdf.js document
    } : null;

    return JSON.stringify({
      version: '1.0',
      floors,
      pdfLayer: serializablePdfLayer,
      annotations: annotations.length > 0 ? annotations : null,
      annotationSettings,
      pdfMeasurements: pdfMeasurements.length > 0 ? pdfMeasurements : null,
      ...extras,
    }, null, 2);
  }, [floors, pdfLayer, annotations, annotationSettings, pdfMeasurements]);

  // Import from JSON - returns { success: boolean, estimates?: array }
  const importFromJSON = useCallback((json) => {
    try {
      const data = JSON.parse(json);
      if (data.floors && Array.isArray(data.floors)) {
        const floors = data.floors.map(normalizeFloor);
        setFloors(floors);
        setActiveFloorId(floors[0]?.id || 'floor-1');
        setSelectedItems([]);
        clearHistory();
        // Restore PDF layer if present
        // Note: pdfDoc won't be restored as it can't be serialized
        // User will need to re-import PDF for page navigation
        if (data.pdfLayer) {
          setPdfLayer({
            ...data.pdfLayer,
            pdfDoc: null,  // pdfDoc wasn't saved, so navigation won't work until re-import
          });
        } else {
          removePdfLayer();
        }
        // Restore annotations if present
        if (data.annotations && Array.isArray(data.annotations)) {
          setAnnotations(data.annotations);
        } else {
          setAnnotations([]);
        }
        // Restore annotation settings if present
        if (data.annotationSettings) {
          setAnnotationSettings(prev => ({ ...prev, ...data.annotationSettings }));
        }
        // Restore PDF measurements if present
        if (data.pdfMeasurements && Array.isArray(data.pdfMeasurements)) {
          setPdfMeasurements(data.pdfMeasurements);
        } else {
          setPdfMeasurements([]);
        }
        // Return success and any estimates found
        return {
          success: true,
          estimates: data.estimates || [],
          invoices: data.invoices || [],
          terrain: data.terrain || null,
          grading: data.grading || null,
        };
      }
      return { success: false };
    } catch (e) {
      console.error('Failed to import JSON:', e);
      return { success: false };
    }
  }, [setFloors, clearHistory, removePdfLayer]);

  return {
    // State
    floors,
    activeFloorId,
    activeFloor,
    selectedItems,
    clipboard,
    hasClipboard: !!clipboard && clipboard.length > 0,
    pdfLayer,

    // Floor management
    setActiveFloorId,
    addFloor,
    removeFloor,
    duplicateFloor,
    renameFloor,
    updateFloorElevation,
    updateActiveFloor,
    updateWindowOnFloor,
    removeWindowOnFloor,
    updateDoorOnFloor,
    removeDoorOnFloor,

    // Selection
    setSelectedItems,

    // Wall operations
    addWall,
    updateWall,
    removeWall,

    // Door operations
    addDoor,
    updateDoor,
    removeDoor,

    // Window operations
    addWindow,
    updateWindow,
    removeWindow,

    // Furniture operations
    addFurniture,
    updateFurniture,
    removeFurniture,

    // Fire & Life Safety operations
    addFireSafety,
    updateFireSafety,
    removeFireSafety,

    // Post operations
    addPost,
    updatePost,
    removePost,

    // Room operations
    addRoom,
    updateRoom,
    removeRoom,
    detectRooms,

    // Roof operations
    addRoof,
    updateRoof,
    removeRoof,

    // Generic operations
    addItem,
    updateItem,
    removeItem,

    // Clipboard
    copySelected,
    paste,
    deleteSelected,

    // File operations
    clearFloor,
    newFile,
    loadFromTemplate,
    exportToJSON,
    importFromJSON,

    // History
    undo,
    redo,
    canUndo,
    canRedo,

    // PDF layer operations
    importPdfLayer,
    updatePdfLayer,
    removePdfLayer,
    calibratePdfScale,
    changePdfPage,
    relinkPdfDocument,

    // PDF page management
    rotatePdfPage,
    deletePdfPages,
    addBlankPdfPage,
    extractPdfPages,

    // Annotation operations
    annotations,
    annotationSettings,
    addAnnotation,
    updateAnnotation,
    removeAnnotation,
    clearAnnotations,
    updateAnnotationSettings,

    // PDF measurement operations
    pdfMeasurements,
    setPdfMeasurements,
    addPdfMeasurement,
    updatePdfMeasurement,
    removePdfMeasurement,
    clearPdfMeasurements,
  };
};

export default useFloorPlan;
